import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { PickupTimeDisplay } from '@/components/order/PickupTimeDisplay';
import type { OrderWithItems } from '@/store/seller';
import type { OrderType } from '@/types';
import { getEffectiveOrderTime } from '@/utils/orderTime';

const orderTypeLabels: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: '마감할인',
  RESERVATION_DISCOUNT: '예약할인', 
  REGULAR_ORDER: '일반주문'
};

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { ToastContainer } = useToast();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  useEffect(() => {
    const loadOrderDetail = async () => {
      if (!orderId) {
        setError('주문 ID가 없습니다');
        setLoading(false);
        return;
      }

      try {
        const result = await sellerService.getOrderDetail(orderId);
        if (result.success && result.data) {
          setOrder(result.data);
        } else {
          setError(result.error || '주문 정보를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Order detail load error:', error);
        setError('주문 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetail();
  }, [orderId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate('/seller/home');
  };


  const handleAccept = () => {
    navigate(`/seller/orders/${orderId}/accept`);
  };

  const handleReject = () => {
    navigate(`/seller/orders/${orderId}/reject`);
  };

  const handleComplete = () => {
    navigate(`/seller/orders/${orderId}/complete`);
  };

  const handleRefund = () => {
    navigate(`/seller/orders/${orderId}/refund`);
  };

  const getActionButtons = () => {
    if (!order) return null;

    switch (order.status) {
      case 'PENDING':
        return (
          <div className="space-y-3">
            <button
              onClick={handleReject}
              className="w-full py-3 px-4 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              주문거부
            </button>
            <button
              onClick={handleAccept}
              className="w-full py-3 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
            >
              주문수락
            </button>
          </div>
        );
      
      case 'ACCEPTED':
        return (
          <div className="space-y-3">
            <button
              onClick={handleRefund}
              className="w-full py-3 px-4 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              환불하기
            </button>
            <button
              onClick={handleComplete}
              className="w-full py-3 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
            >
              준비완료
            </button>
          </div>
        );
      
      case 'READY':
      case 'COMPLETED':
        return (
          <button
            onClick={handleRefund}
            className="w-full py-3 px-4 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            환불하기
          </button>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <SellerPageContainer activeNavId="home">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  if (error || !order) {
    return (
      <SellerPageContainer activeNavId="home">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-gray-600 mb-4">{error || '주문 정보를 찾을 수 없습니다'}</p>
            <button
              onClick={handleBack}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="home">
      <div className="pb-4">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">주문 상세</h1>
        </div>

        {/* 주문 정보 카드 */}
        <div className="bg-white rounded-lg p-4 mb-6">
          {/* 주문 유형 */}
          <div className="mb-4">
            <span className="text-sm text-gray-600">주문유형</span>
            <p className="text-lg font-medium">{orderTypeLabels[order.order_type]}</p>
          </div>

          {/* 주문자 정보 */}
          <div className="mb-4">
            <span className="text-sm text-gray-600">주문자명</span>
            <p className="text-lg font-medium">{order.buyer_nickname}</p>
          </div>

          {/* 주문번호 */}
          <div className="mb-4">
            <span className="text-sm text-gray-600">주문번호</span>
            <p className="text-lg font-medium">{order.order_number}</p>
          </div>

          {/* 주문일시 */}
          <div className="mb-4">
            <span className="text-sm text-gray-600">주문일시</span>
            <p className="text-lg font-medium">{formatDateTime(getEffectiveOrderTime(order) || order.created_at)}</p>
          </div>

          {/* 주문방법 */}
          <div className="mb-4">
            <span className="text-sm text-gray-600">주문방법</span>
            <p className="text-lg font-medium">{orderTypeLabels[order.order_type]}</p>
          </div>

          {/* 수령일시 */}
          <div className="mb-6">
            <span className="text-sm text-gray-600">수령일시</span>
            <div className="mt-1">
              <PickupTimeDisplay 
                order={{
                  ...order,
                  items: order.items?.map(item => ({
                    ...item,
                    product: item.products ? { id: '', name: item.products.name, original_price: 0 } : undefined
                  }))
                } as any}
                showDescription={true}
                className="text-lg font-medium text-blue-600"
              />
            </div>
          </div>

          {/* 주문 상품 목록 */}
          <div className="border-t pt-4">
            <h3 className="font-medium mb-3">주문 상품</h3>
            <div className="space-y-2">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2">
                  <div className="flex-1">
                    <span className="font-medium">{item.product_name}</span>
                    <span className="text-gray-600 ml-2">x{item.quantity}</span>
                  </div>
                  <span className="font-medium">{formatCurrency((item.price || item.unit_price || 0) * item.quantity)}원</span>
                </div>
              ))}
            </div>
            
            {/* 총합계 */}
            <div className="border-t mt-4 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">합계</span>
                <span className="text-xl font-bold text-green-600">
                  {formatCurrency(order.total_amount)}원
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 액션 버튼들 */}
        {getActionButtons()}
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}
